import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Plus, Edit2, Trash2, Save, ChevronUp, ChevronDown, AlertCircle } from "lucide-react";
import {
  staffCustomFieldsApi,
  type StaffCustomField,
  type CustomFieldType,
} from "@/lib/api/staff-custom-fields";
import { cn } from "@/lib/utils";

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio" },
  { value: "textarea", label: "Textarea" },
  { value: "date", label: "Date" },
  { value: "file", label: "File Upload" },
];

const StaffCustomFieldsSettingsPage = () => {
  const { user } = useAuth();
  const { tenant, isLoading: tenantLoading } = useTenant();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customFields, setCustomFields] = useState<StaffCustomField[]>([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<StaffCustomField | null>(null);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  // Get tenant ID from user membership as fallback if tenant context doesn't have it
  const tenantId = tenant?.id || user?.memberships?.[0]?.tenantId;

  useEffect(() => {
    // Wait for tenant to finish loading (but not indefinitely - use user membership as fallback)
    if (tenantLoading && !user?.memberships?.[0]?.tenantId) {
      return;
    }

    // If we have a tenant ID (from context or user membership), load custom fields
    if (isTenantAdmin && tenantId) {
      loadCustomFields();
    } else if (!isTenantAdmin) {
      setLoading(false);
    } else if (!tenantId) {
      // No tenant ID available after loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTenantAdmin, tenantId, tenantLoading]);

  const loadCustomFields = async () => {
    const currentTenantId = tenantId;
    if (!currentTenantId) {
      console.warn("Cannot load custom fields: tenant ID not available");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      console.log("Loading custom fields for tenant:", currentTenantId);
      const config = await staffCustomFieldsApi.getConfig(currentTenantId);
      console.log("Custom fields config loaded:", config);
      setCustomFields(config.customFields || []);
      // Expand all fields by default
      setExpandedFields(new Set((config.customFields || []).map((f) => f.id)));
    } catch (error: any) {
      console.error("Failed to load custom fields:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      toast.error(
        error.response?.data?.message ||
          `Failed to load custom fields: ${error.message || "Unknown error"}`
      );
      // Set empty array on error so UI can still render
      setCustomFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const currentTenantId = tenantId;
    if (!currentTenantId) {
      toast.error("Tenant ID not available. Please refresh the page.");
      return;
    }
    try {
      setSaving(true);
      await staffCustomFieldsApi.updateConfig(currentTenantId, { customFields });
      toast.success("Custom fields configuration saved successfully!");
      await loadCustomFields();
    } catch (error: any) {
      console.error("Failed to save custom fields:", error);
      toast.error(error.response?.data?.message || "Failed to save custom fields configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = () => {
    const newField: StaffCustomField = {
      id: `field-${Date.now()}`,
      name: "",
      label: "",
      type: "text",
      required: false,
      order: customFields.length,
    };
    setEditingField(newField);
    setShowFieldModal(true);
  };

  const handleEditField = (field: StaffCustomField) => {
    setEditingField({ ...field });
    setShowFieldModal(true);
  };

  const handleDeleteField = (fieldId: string) => {
    if (!confirm("Are you sure you want to delete this field?")) return;
    setCustomFields(customFields.filter((f) => f.id !== fieldId));
  };

  const handleMoveField = (fieldId: string, direction: "up" | "down") => {
    const index = customFields.findIndex((f) => f.id === fieldId);
    if (index === -1) return;

    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === customFields.length - 1) return;

    const newFields = [...customFields];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];

    // Update order
    newFields.forEach((f, i) => {
      f.order = i;
    });

    setCustomFields(newFields);
  };

  const handleSaveField = () => {
    if (!editingField || !editingField.label || !editingField.name) {
      toast.error("Please fill in label and field name");
      return;
    }

    // Validate field name (must be valid identifier)
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(editingField.name)) {
      toast.error(
        "Field name must start with a letter and contain only letters, numbers, and underscores"
      );
      return;
    }

    // Check for duplicate names
    const existingField = customFields.find(
      (f) => f.name === editingField.name && f.id !== editingField.id
    );
    if (existingField) {
      toast.error("A field with this name already exists");
      return;
    }

    const updatedFields = [...customFields];
    const existingIndex = updatedFields.findIndex((f) => f.id === editingField.id);

    if (existingIndex >= 0) {
      updatedFields[existingIndex] = editingField;
    } else {
      updatedFields.push({ ...editingField, order: updatedFields.length });
    }

    // Sort by order
    updatedFields.sort((a, b) => a.order - b.order);
    updatedFields.forEach((f, i) => {
      f.order = i;
    });

    setCustomFields(updatedFields);
    setShowFieldModal(false);
    setEditingField(null);
  };

  const toggleFieldExpanded = (fieldId: string) => {
    const newSet = new Set(expandedFields);
    if (newSet.has(fieldId)) {
      newSet.delete(fieldId);
    } else {
      newSet.add(fieldId);
    }
    setExpandedFields(newSet);
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage custom fields.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Show loading while tenant is loading (but allow fallback to user membership)
  if ((tenantLoading && !user?.memberships?.[0]?.tenantId) || loading) {
    return (
      <AppLayout>
        <PageContainer width="wide">
          <PageHeader
            title="Staff Custom Fields"
            description="Configure custom fields for staff member creation and management"
            breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Staff Custom Fields" }]}
            showSearch={false}
          />
          <Card className="p-6 bg-white shadow-sm">
            <div className="py-16">
              <LoadingSpinner
                size="lg"
                label={tenantLoading ? "Loading tenant..." : "Loading custom fields..."}
              />
            </div>
          </Card>
        </PageContainer>
      </AppLayout>
    );
  }

  // Show error if tenant ID is not available from any source
  if (!tenantId) {
    return (
      <AppLayout>
        <PageContainer width="wide">
          <PageHeader
            title="Staff Custom Fields"
            description="Configure custom fields for staff member creation and management"
            breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Staff Custom Fields" }]}
            showSearch={false}
          />
          <Card className="p-6 bg-white shadow-sm">
            <div className="py-16 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Tenant Not Found</h3>
              <p className="mb-4 text-gray-500">
                Unable to load tenant information. Please refresh the page or contact support.
              </p>
              <Button onClick={() => window.location.reload()} variant="outline">
                Refresh Page
              </Button>
            </div>
          </Card>
        </PageContainer>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Staff Custom Fields"
          description="Configure custom fields for staff member creation and management"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Staff Custom Fields" }]}
          showSearch={false}
        />

        <div className="flex justify-end mb-6">
          <Button onClick={handleAddField} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Custom Field
          </Button>
        </div>

        <Card className="p-6 bg-white shadow-sm">
          {customFields.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Plus className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">No custom fields yet</h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                Add custom fields to collect additional information when creating staff members.
              </p>
              <Button onClick={handleAddField} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Field
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {customFields.map((field, index) => {
                const isExpanded = expandedFields.has(field.id);
                return (
                  <div
                    key={field.id}
                    className="border rounded-lg transition-colors"
                    style={{
                      borderColor: isExpanded ? "#E5E7EB" : "#F3F4F6",
                      backgroundColor: isExpanded ? "#F9FAFB" : "white",
                    }}
                  >
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleFieldExpanded(field.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-gray-900">{field.label}</span>
                          <span className="text-xs text-gray-500">
                            {field.name} • {FIELD_TYPES.find((t) => t.value === field.type)?.label}
                            {field.required && " • Required"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveField(field.id, "up");
                          }}
                          disabled={index === 0}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveField(field.id, "down");
                          }}
                          disabled={index === customFields.length - 1}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditField(field);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteField(field.id);
                          }}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 ml-2" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 ml-2" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {customFields.length > 0 && (
            <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          )}
        </Card>
      </PageContainer>

      {/* Field Modal */}
      {showFieldModal && editingField && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                {editingField.id.startsWith("field-") ? "Add Custom Field" : "Edit Custom Field"}
              </h3>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="field-label" className="flex items-center gap-1 mb-2">
                    Label <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="field-label"
                    value={editingField.label}
                    onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                    placeholder="e.g., Employee Number"
                  />
                </div>

                <div>
                  <Label htmlFor="field-name" className="flex items-center gap-1 mb-2">
                    Field Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="field-name"
                    value={editingField.name}
                    onChange={(e) =>
                      setEditingField({
                        ...editingField,
                        name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                      })
                    }
                    placeholder="e.g., employeeNumber"
                    disabled={!editingField.id.startsWith("field-")}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Must start with a letter and contain only letters, numbers, and underscores
                  </p>
                </div>

                <div>
                  <Label htmlFor="field-type" className="mb-2">
                    Field Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={editingField.type}
                    onValueChange={(value: CustomFieldType) =>
                      setEditingField({ ...editingField, type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="field-required"
                    checked={editingField.required}
                    onCheckedChange={(checked) =>
                      setEditingField({ ...editingField, required: checked === true })
                    }
                  />
                  <Label htmlFor="field-required" className="cursor-pointer">
                    Required field
                  </Label>
                </div>

                <div>
                  <Label htmlFor="field-placeholder" className="mb-2">
                    Placeholder
                  </Label>
                  <Input
                    id="field-placeholder"
                    value={editingField.placeholder || ""}
                    onChange={(e) =>
                      setEditingField({ ...editingField, placeholder: e.target.value })
                    }
                    placeholder="e.g., Enter employee number"
                  />
                </div>

                {(editingField.type === "select" || editingField.type === "radio") && (
                  <div>
                    <Label className="mb-2">Options</Label>
                    <div className="space-y-2">
                      {(editingField.options || []).map((option, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            placeholder="Label"
                            value={option.label}
                            onChange={(e) => {
                              const newOptions = [...(editingField.options || [])];
                              newOptions[idx] = { ...option, label: e.target.value };
                              setEditingField({ ...editingField, options: newOptions });
                            }}
                          />
                          <Input
                            placeholder="Value"
                            value={option.value}
                            onChange={(e) => {
                              const newOptions = [...(editingField.options || [])];
                              newOptions[idx] = { ...option, value: e.target.value };
                              setEditingField({ ...editingField, options: newOptions });
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newOptions =
                                editingField.options?.filter((_, i) => i !== idx) || [];
                              setEditingField({ ...editingField, options: newOptions });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [
                            ...(editingField.options || []),
                            { label: "", value: "" },
                          ];
                          setEditingField({ ...editingField, options: newOptions });
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Option
                      </Button>
                    </div>
                  </div>
                )}

                {editingField.type === "number" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="field-min" className="mb-2">
                        Minimum Value
                      </Label>
                      <Input
                        id="field-min"
                        type="number"
                        value={editingField.validation?.min || ""}
                        onChange={(e) =>
                          setEditingField({
                            ...editingField,
                            validation: {
                              ...editingField.validation,
                              min: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="field-max" className="mb-2">
                        Maximum Value
                      </Label>
                      <Input
                        id="field-max"
                        type="number"
                        value={editingField.validation?.max || ""}
                        onChange={(e) =>
                          setEditingField({
                            ...editingField,
                            validation: {
                              ...editingField.validation,
                              max: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {(editingField.type === "text" || editingField.type === "textarea") && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="field-min-length" className="mb-2">
                        Min Length
                      </Label>
                      <Input
                        id="field-min-length"
                        type="number"
                        value={editingField.validation?.minLength || ""}
                        onChange={(e) =>
                          setEditingField({
                            ...editingField,
                            validation: {
                              ...editingField.validation,
                              minLength: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="field-max-length" className="mb-2">
                        Max Length
                      </Label>
                      <Input
                        id="field-max-length"
                        type="number"
                        value={editingField.validation?.maxLength || ""}
                        onChange={(e) =>
                          setEditingField({
                            ...editingField,
                            validation: {
                              ...editingField.validation,
                              maxLength: e.target.value ? Number(e.target.value) : undefined,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowFieldModal(false);
                    setEditingField(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveField}>
                  <Save className="w-4 h-4 mr-2" />
                  {editingField.id.startsWith("field-") ? "Add Field" : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </AppLayout>
  );
};

export default StaffCustomFieldsSettingsPage;
